import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Target, Eye, ShieldCheck, ArrowUpRight, CheckCircle2, HeartHandshake } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import { BRAND, CERTIFICATIONS, img } from "@/config";

const TIMELINE = [
  { year: "2014", title: "Founded in Jaipur", body: "KAS Info Tech begins delivering CCTV and networking solutions to local businesses." },
  { year: "2017", title: "Government projects", body: "Empanelled for public-sector work; first large-scale surveillance deployments." },
  { year: "2020", title: "Statewide scale", body: "Secured 850+ police stations — a landmark statewide surveillance network." },
  { year: "2023", title: "Enterprise & healthcare", body: "Expanded into HMS, enterprise networking and corporate security for national brands." },
  { year: "2026", title: "900+ installations", body: "Pan-India presence with a proven track record across every major sector." },
];

const LEADERS = [
  { name: "Managing Director", role: "Strategy & Government Relations", image: "tech_support" },
  { name: "Technical Director", role: "Surveillance & Networking", image: "server_room" },
  { name: "Head of Operations", role: "Project Delivery & Support", image: "networking" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About KAS Info Tech"
        titleLines={["A decade of securing", "India's infrastructure."]}
        subtitle={`Since ${BRAND.established}, KAS Info Tech has delivered reliable, scalable security and IT infrastructure to government and corporate clients — with government-grade reliability and 24/7 support.`}
        imageKey="server_room"
      />

      {/* Mission / Vision */}
      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-24">
        <div className="grid gap-6 lg:grid-cols-2">
          {[
            { Icon: Target, tag: "Mission", text: "To deliver dependable, scalable security infrastructure that protects people, assets and data — on time, at any scale." },
            { Icon: Eye, tag: "Vision", text: "To be India's most trusted security & surveillance partner for government and enterprise, setting the standard for reliability." },
          ].map((item, i) => (
            <Reveal key={item.tag} delay={i * 0.12}>
              <div className="h-full rounded-3xl border border-slate-800 bg-[#111c33]/40 p-10">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-800 bg-white/5 text-[#60A5FA]"><item.Icon className="h-6 w-6" /></span>
                <p className="mt-6 text-sm font-semibold uppercase tracking-widest text-slate-500">{item.tag}</p>
                <p className="mt-4 font-display text-2xl font-bold leading-snug tracking-tight text-white lg:text-3xl">{item.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="mx-auto max-w-7xl px-6 pb-20 lg:px-10 lg:pb-24">
        <Reveal><h2 className="font-display text-3xl font-extrabold tracking-tight text-white lg:text-5xl">Our journey</h2></Reveal>
        <div className="mt-12">
          {TIMELINE.map((t, i) => (
            <Reveal key={t.year} delay={i * 0.06}>
              <div className="grid items-start gap-4 border-t border-slate-800 py-8 lg:grid-cols-12">
                <span className="font-display text-3xl font-extrabold text-[#2563EB] lg:col-span-2">{t.year}</span>
                <h3 className="font-display text-xl font-bold text-white lg:col-span-4">{t.title}</h3>
                <p className="max-w-lg text-base leading-relaxed text-slate-400 lg:col-span-6">{t.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Leadership */}
      <section className="mx-auto max-w-7xl px-6 pb-20 lg:px-10 lg:pb-24" data-testid="leadership-section">
        <Reveal><h2 className="font-display text-3xl font-extrabold tracking-tight text-white lg:text-5xl">Leadership team</h2></Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {LEADERS.map((l, i) => (
            <Reveal key={l.name} delay={i * 0.08}>
              <motion.div whileHover={{ y: -6 }} className="group overflow-hidden rounded-3xl border border-slate-800 bg-[#111c33]/40">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={img(l.image)} alt={l.name} className="h-full w-full object-cover opacity-80 transition-all duration-500 group-hover:scale-105 group-hover:opacity-100" />
                </div>
                <div className="p-6">
                  <p className="font-display text-lg font-bold text-white">{l.name}</p>
                  <p className="mt-1 text-sm text-[#60A5FA]">{l.role}</p>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Certifications + CSR */}
      <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
        <div className="grid gap-6 lg:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-3xl border border-slate-800 bg-[#111c33]/40 p-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#2563EB]/15 text-[#60A5FA]"><ShieldCheck className="h-6 w-6" /></span>
              <h3 className="mt-5 font-display text-2xl font-bold text-white">Certifications &amp; partnerships</h3>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {CERTIFICATIONS.map((c) => (
                  <li key={c} className="flex items-center gap-2.5 text-slate-300"><CheckCircle2 className="h-4 w-4 text-[#2563EB]" /> <span className="text-sm">{c}</span></li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl border border-slate-800 bg-[#111c33]/40 p-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#2563EB]/15 text-[#60A5FA]"><HeartHandshake className="h-6 w-6" /></span>
              <h3 className="mt-5 font-display text-2xl font-bold text-white">Community initiatives</h3>
              <p className="mt-4 text-base leading-relaxed text-slate-400">We support digital-literacy drives and provide subsidised IT lab setups for schools in underserved regions — because safer, smarter communities start with access to technology.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 pb-28 lg:px-10">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-6 rounded-[2rem] border border-slate-800 bg-[#111c33] p-10 lg:flex-row lg:items-center lg:p-14">
            <h2 className="max-w-xl font-display text-3xl font-extrabold tracking-tight text-white lg:text-4xl">Partner with a team the government trusts.</h2>
            <Link to="/contact" data-testid="about-cta-button" className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-[#2563EB] px-7 py-4 text-base font-semibold text-white transition-colors hover:bg-[#3b82f6]">Get a Free Site Survey <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
